/**
 * Documentation Index for Help Center
 *
 * This file exports the document manifest and search configuration
 * for the Fuse.js-powered search functionality.
 */

// Import content from quickstart guides
import { adminQuickStartContent } from './quickstart-admin';
import { managerQuickStartContent } from './quickstart-manager';
import { tlQuickStartContent } from './quickstart-tl';

// Import content from feature documentation
import { rulesContent } from './rules';
import { notificationsContent } from './notifications';
import { activityLogsContent } from './activity-logs';

// Content registry - maps doc IDs to their content
const contentRegistry = {
  'quickstart-admin': adminQuickStartContent,
  'quickstart-manager': managerQuickStartContent,
  'quickstart-tl': tlQuickStartContent,
  'rules-overview': rulesContent,
  'notifications-overview': notificationsContent,
  'activity-logs-overview': activityLogsContent,
};

// Document index for Fuse.js search
export const documentIndex = [
  // Quick Start Guides
  {
    id: 'quickstart-admin',
    title: 'Admin Quick Start Guide',
    description: 'Get started as an admin: configure rules, manage notifications, and monitor activity.',
    category: 'quickstart',
    roles: ['admin'],
    keywords: [
      'getting started', 'admin', 'setup', 'configuration', 'first steps',
      'how to start', 'new admin', 'onboarding', 'tutorial'
    ],
    path: '@/data/docs/content/quickstart-admin'
  },
  {
    id: 'quickstart-manager',
    title: 'Manager Quick Start Guide',
    description: 'Get started as a manager: track team performance, verify commitments, view analytics.',
    category: 'quickstart',
    roles: ['manager'],
    keywords: [
      'getting started', 'manager', 'team', 'performance', 'first steps',
      'how to start', 'new manager', 'onboarding', 'tutorial'
    ],
    path: '@/data/docs/content/quickstart-manager'
  },
  {
    id: 'quickstart-tl',
    title: 'Team Leader Quick Start Guide',
    description: 'Get started as a team leader: enter pulses, manage commitments, track daily progress.',
    category: 'quickstart',
    roles: ['team_leader'],
    keywords: [
      'getting started', 'team leader', 'TL', 'pulse', 'commitments',
      'first steps', 'how to start', 'onboarding', 'tutorial', 'daily workflow'
    ],
    path: '@/data/docs/content/quickstart-tl'
  },

  // Feature Documentation
  {
    id: 'rules-overview',
    title: 'Rules Engine Overview',
    description: 'Learn how to create, configure, and manage notification rules that automate your workflow.',
    category: 'rules',
    roles: ['admin'],
    keywords: [
      'rules', 'automation', 'notifications', 'conditions', 'triggers',
      'how to create rule', 'rule configuration', 'alert rules', 'automatic notifications',
      'when to send', 'rule logic', 'conditions', 'actions'
    ],
    path: '@/data/docs/content/rules-overview'
  },
  {
    id: 'notifications-overview',
    title: 'Notifications System Guide',
    description: 'Understand how notifications work: scheduled jobs, delivery channels, and troubleshooting.',
    category: 'notifications',
    roles: ['admin'],
    keywords: [
      'notifications', 'alerts', 'email', 'whatsapp', 'scheduled',
      'how notifications work', 'delivery', 'jobs', 'notification settings',
      'not receiving notifications', 'troubleshooting', 'send notification'
    ],
    path: '@/data/docs/content/notifications-overview'
  },
  {
    id: 'activity-logs-overview',
    title: 'Activity Logs Guide',
    description: 'Track and audit all system activity: who did what, when, and the outcome.',
    category: 'activity',
    roles: ['admin'],
    keywords: [
      'activity', 'logs', 'audit', 'tracking', 'history',
      'who did what', 'changes', 'audit trail', 'user activity',
      'system logs', 'actions', 'events'
    ],
    path: '@/data/docs/content/activity-logs-overview'
  },

  // General Documentation
  {
    id: 'pulse-entry-guide',
    title: 'Pulse Entry Guide',
    description: 'How to enter pulse data: timing windows, required fields, and best practices.',
    category: 'general',
    roles: ['admin', 'team_leader'],
    keywords: [
      'pulse', 'entry', 'data entry', 'P1', 'P2', 'P3', 'P4',
      'how to enter pulse', 'timing', 'windows', 'submit pulse',
      'daily entry', 'sales data'
    ],
    path: '@/data/docs/content/pulse-entry-guide'
  },
  {
    id: 'commitment-guide',
    title: 'Commitments & Verification',
    description: 'Understanding the commitment workflow: creating closures, verification process, fulfillment tracking.',
    category: 'general',
    roles: ['admin', 'manager', 'team_leader'],
    keywords: [
      'commitment', 'closure', 'verification', 'verify', 'fulfillment',
      'how to verify', 'commitment workflow', 'sales closure',
      'revenue', 'targets', 'verify commitment'
    ],
    path: '@/data/docs/content/commitment-guide'
  },
  {
    id: 'dashboard-guide',
    title: 'Dashboard Analytics',
    description: 'Understand your dashboards: KPIs, drill-down analysis, and performance insights.',
    category: 'general',
    roles: ['admin', 'manager', 'team_leader'],
    keywords: [
      'dashboard', 'analytics', 'KPI', 'metrics', 'performance',
      'insights', 'drill-down', 'charts', 'graphs', 'reports',
      'command center', 'overview'
    ],
    path: '@/data/docs/content/dashboard-guide'
  }
];

/**
 * Get a document by its ID
 * @param {string} docId - The document identifier
 * @returns {Object|undefined} The document object with content, or undefined if not found
 */
export function getDocById(docId) {
  const doc = documentIndex.find(doc => doc.id === docId);
  if (!doc) return undefined;

  // Attach content if available in registry
  const content = contentRegistry[docId];
  if (content) {
    return { ...doc, content: content.sections };
  }
  return doc;
}

/**
 * Get content for a document by ID
 * @param {string} docId - The document identifier
 * @returns {Array|undefined} The sections array or undefined
 */
export function getContentById(docId) {
  const content = contentRegistry[docId];
  return content ? content.sections : undefined;
}

/**
 * Get documents by category
 * @param {string} category - The category name
 * @returns {Array} Array of documents in that category
 */
export function getDocsByCategory(category) {
  return documentIndex.filter(doc => doc.category === category);
}

/**
 * Get documents accessible to a specific role
 * @param {string} role - The user role
 * @returns {Array} Array of documents accessible to that role
 */
export function getDocsForRole(role) {
  return documentIndex.filter(doc => doc.roles.includes(role));
}

/**
 * Fuse.js search configuration
 * - keys: weighted search fields (title most important, then description, then keywords)
 * - threshold: 0.4 = forgiving for typos (0 = exact, 1 = match anything)
 * - ignoreLocation: true = match anywhere in string, not just start
 * - includeScore: true = include relevance score for ranking
 * - includeMatches: true = include match positions for highlighting
 */
export const fuseOptions = {
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'description', weight: 0.3 },
    { name: 'keywords', weight: 0.2 },
    { name: 'category', weight: 0.1 }
  ],
  threshold: 0.4,
  ignoreLocation: true,
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 2
};

export default documentIndex;
